import { Alert } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  closeSystemNotification,
  deployAtFrom,
  formatRemaining,
  isDeployCancelled,
  isStale,
  minutesLeft,
  readStoredDeployAt,
  storeDeployAt,
  updateSystemNotification,
} from './deployWarning';

/** Counts down to an announced deploy, in the page and in the system notification. */
const DeployCountdownBanner: React.FC = () => {
  const { t } = useTranslation();
  const [deployAt, setDeployAt] = useState<number | null>(() => readStoredDeployAt(Date.now()));
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event: MessageEvent) => {
      // The cancel push replaces the system notification itself; stopping here also stops
      // the countdown from overwriting it.
      if (isDeployCancelled(event.data?.data)) {
        storeDeployAt(null);
        setDeployAt(null);
        return;
      }
      const at = deployAtFrom(event.data?.data, Date.now());
      if (at === null) return;
      storeDeployAt(at);
      setNow(Date.now());
      setDeployAt(at);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (deployAt === null) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [deployAt]);

  const remaining = deployAt === null ? 0 : deployAt - now;
  const minutes = minutesLeft(remaining);
  const stale = deployAt !== null && isStale(deployAt, now);

  useEffect(() => {
    if (!stale) return;
    storeDeployAt(null);
    setDeployAt(null);
    void closeSystemNotification();
  }, [stale]);

  // One step per minute, so the system notification counts down without spamming.
  useEffect(() => {
    if (deployAt === null || stale) return;
    const body = minutes > 0 ? t('deploy-warning-minutes', { count: minutes }) : t('deploy-warning-now');
    void updateSystemNotification(t('deploy-warning-title'), body);
  }, [deployAt, minutes, stale, t]);

  if (deployAt === null || stale) return null;

  return (
    <Alert
      banner
      showIcon
      closable
      type={remaining > 0 ? 'warning' : 'info'}
      message={
        remaining > 0 ? t('deploy-warning-countdown', { time: formatRemaining(remaining) }) : t('deploy-warning-now')
      }
      onClose={() => {
        storeDeployAt(null);
        setDeployAt(null);
      }}
    />
  );
};

export default DeployCountdownBanner;
